/**
 * Frontend data service - calls backend API
 */

import { apiService } from './ApiService.js'

class DataService {
  constructor() {
    console.log('Frontend DataService initialized - calls backend API')
  }

  async getCustomers(limit = null, search = null) {
    try {
      return await apiService.getCustomers(limit, search)
    } catch (error) {
      console.error('Error fetching customers:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getContactsByCustomer(customerId) {
    try {
      return await apiService.getContactsByCustomer(customerId)
    } catch (error) {
      console.error('Error fetching contacts:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getProducts(searchTerm = '', limit = null) {
    try {
      return await apiService.getProducts(searchTerm, limit)
    } catch (error) {
      console.error('Error fetching products:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getDesigns(searchTerm = '', limit = 100) {
    try {
      return await apiService.getDesigns(searchTerm, limit)
    } catch (error) {
      console.error('Error fetching designs:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getProspects(search = null) {
    try {
      return await apiService.getProspects(search)
    } catch (error) {
      console.error('Error fetching prospects:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getProduct(productId) {
    try {
      return await apiService.getProduct(productId)
    } catch (error) {
      console.error('Error fetching product:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async getProductAttributes(productNumber, limit = null) {
    try {
      return await apiService.getProductAttributes(productNumber, limit)
    } catch (error) {
      console.error('Error fetching product attributes:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getProductOrderSettings(productNumber) {
    try {
      return await apiService.getProductOrderSettings(productNumber)
    } catch (error) {
      console.error('Error fetching product order settings:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async getOpportunities() {
    try {
      return await apiService.getOpportunities()
    } catch (error) {
      console.error('Error fetching opportunities:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getQuotations() {
    try {
      return await apiService.getQuotations()
    } catch (error) {
      console.error('Error fetching quotations:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getQuotationsPaginated(page = 1, pageSize = 20, filter = '', orderBy = '', creatorEmail = '') {
    try {
      return await apiService.getQuotationsPaginated(page, pageSize, filter, orderBy, creatorEmail)
    } catch (error) {
      console.error('Error fetching paginated quotations:', error)
      return {
        data: [],
        totalCount: 0,
        currentPage: page,
        pageSize,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
        success: false,
        error: error.message
      }
    }
  }

  async getMyQuotationsPaginated(page = 1, pageSize = 20, filter = '', orderBy = '') {
    try {
      return await apiService.getMyQuotationsPaginated(page, pageSize, filter, orderBy)
    } catch (error) {
      console.error('Error fetching my quotations:', error)
      return {
        data: [],
        totalCount: 0,
        currentPage: page,
        pageSize,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
        success: false,
        error: error.message
      }
    }
  }

  async getQuotationByNumber(quotationNumber) {
    try {
      return await apiService.getQuotationByNumber(quotationNumber)
    } catch (error) {
      console.error('Error fetching quotation by number:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async createQuotation(quotationData) {
    try {
      return await apiService.createQuotation(quotationData)
    } catch (error) {
      console.error('Error creating quotation:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async updateQuotation(quotationId, updates) {
    try {
      return await apiService.updateQuotation(quotationId, updates)
    } catch (error) {
      console.error('Error updating quotation:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async duplicateQuotation(quotationData) {
    try {
      // Step 1: Create the duplicate quotation header
      // Remove all entity reference properties and system fields more thoroughly
      const duplicateQuote = Object.keys(quotationData).reduce((acc, key) => {
        // Exclude entity reference properties, navigation properties, system fields, and problematic OptionSet fields
        if (key.includes('_value') || 
            key.startsWith('_') ||
            key.includes('@odata') ||
            ['id', 'createdon', 'modifiedon', 'createdby', 'modifiedby', 'versionnumber', 'mserp_salesquotationnumber', 'mserp_deliveryaddresstimezone'].includes(key)) {
          console.log(`Filtering out field: ${key}`);
          return acc;
        }
        // Skip fields with null or empty values that are OptionSet fields
        if (quotationData[key] === null || quotationData[key] === '') {
          console.log(`Skipping null/empty field: ${key}`);
          return acc;
        }
        acc[key] = quotationData[key];
        return acc;
      }, {});
      
      // Set specific fields for the duplicate
      duplicateQuote.mserp_alg_salesquotationname = `${quotationData.mserp_alg_salesquotationname || quotationData.mserp_salesquotationname} (Copy)`;
      duplicateQuote.mserp_salesquotationstatus = 200000000; // Reset to "Created" status
      
      // Check and update expiration date if it's in the past
      if (duplicateQuote.mserp_salesquotationexpirydate) {
        const today = new Date();
        const expiryDate = new Date(duplicateQuote.mserp_salesquotationexpirydate);
        
        // Set time to start of day for comparison
        today.setHours(0, 0, 0, 0);
        expiryDate.setHours(0, 0, 0, 0);
        
        if (expiryDate < today) {
          // Set expiry date to today + 7 days if it's in the past
          const newExpiryDate = new Date(today);
          newExpiryDate.setDate(today.getDate() + 7);
          duplicateQuote.mserp_salesquotationexpirydate = newExpiryDate.toISOString();
          console.log('Updated expiry date to today + 7 days for duplicate quotation');
        }
      }
      
      // Check and update follow-up date if it's in the past
      if (duplicateQuote.mserp_salesquotationfollowupdate) {
        const today = new Date();
        const followUpDate = new Date(duplicateQuote.mserp_salesquotationfollowupdate);
        
        // Set time to start of day for comparison
        today.setHours(0, 0, 0, 0);
        followUpDate.setHours(0, 0, 0, 0);
        
        if (followUpDate < today) {
          // Set follow-up date to today + 1 day if it's in the past
          const newFollowUpDate = new Date(today);
          newFollowUpDate.setDate(today.getDate() + 1);
          duplicateQuote.mserp_salesquotationfollowupdate = newFollowUpDate.toISOString();
          console.log('Updated follow-up date to today + 1 day for duplicate quotation');
        }
      }
      
      console.log('Filtered duplicate quote data:', duplicateQuote);
      
      // Create the new quotation header
      console.log('About to create quotation with data:', duplicateQuote)
      const newQuotationResult = await apiService.createQuotation(duplicateQuote)
      console.log('Quotation creation result:', newQuotationResult)
      
      if (!newQuotationResult.success) {
        throw new Error(newQuotationResult.error || 'Failed to create duplicate quotation')
      }
      
      const newQuotation = newQuotationResult.data
      const newQuotationNumber = newQuotation.mserp_salesquotationnumber
      
      // Step 2: Try to retrieve the newly created quotation to get proper IDs and references
      // If this fails, we'll use the original creation response data
      let retrievedQuotation = null
      const retrievedQuotationResult = await apiService.getQuotationsPaginated(1, 1, `mserp_salesquotationnumber eq '${newQuotationNumber}'`)
      
      if (retrievedQuotationResult.success && retrievedQuotationResult.data && retrievedQuotationResult.data.length > 0) {
        retrievedQuotation = retrievedQuotationResult.data[0]
        console.log('Successfully retrieved newly created quotation')
      } else {
        console.warn('Could not retrieve newly created quotation, using original creation response data')
        retrievedQuotation = newQuotation // Use the creation response data instead
      }
      
      console.log('Full retrieved quotation data:', retrievedQuotation)
      console.log('Available fields in retrieved quotation:', Object.keys(retrievedQuotation))
      
      // Try different possible ID field names
      const possibleIdFields = [
        'id',
        'mserp_salesquotationheaderv2entityid',
        'mserp_salesquotationheaderv2id', 
        '@odata.id'
      ];
      
      let newQuotationId = null;
      for (const fieldName of possibleIdFields) {
        if (retrievedQuotation[fieldName]) {
          console.log(`Found quotation ID in field '${fieldName}':`, retrievedQuotation[fieldName]);
          newQuotationId = retrievedQuotation[fieldName];
          break;
        }
      }
      
      // If @odata.id contains the ID, extract it
      if (!newQuotationId && retrievedQuotation['@odata.id']) {
        const odataIdMatch = retrievedQuotation['@odata.id'].match(/\(([^)]+)\)/);
        if (odataIdMatch) {
          newQuotationId = odataIdMatch[1];
          console.log('Extracted ID from @odata.id:', newQuotationId);
        }
      }
      
      // If we still don't have an ID, try to get it from the original creation response
      if (!newQuotationId) {
        console.warn('Could not find quotation ID in retrieved data, trying original creation response...');
        console.log('Original creation data:', newQuotation);
        
        // Try to get ID from original creation response
        const possibleOriginalIdFields = [
          'id',
          'mserp_salesquotationheaderv2entityid',
          '@odata.id'
        ];
        
        for (const fieldName of possibleOriginalIdFields) {
          if (newQuotation[fieldName]) {
            console.log(`Found quotation ID in original creation field '${fieldName}':`, newQuotation[fieldName]);
            newQuotationId = newQuotation[fieldName];
            break;
          }
        }
        
        // Extract from @odata.id if available
        if (!newQuotationId && newQuotation['@odata.id']) {
          const odataIdMatch = newQuotation['@odata.id'].match(/\(([^)]+)\)/);
          if (odataIdMatch) {
            newQuotationId = odataIdMatch[1];
            console.log('Extracted ID from original @odata.id:', newQuotationId);
          }
        }
      }
      
      console.log('Final quotation ID:', newQuotationId);
      console.log('Retrieved quotation number:', retrievedQuotation.mserp_salesquotationnumber)
      
      if (!newQuotationId) {
        console.error('Could not determine quotation ID from any source!');
        console.error('This will cause issues with line creation');
        // Don't throw error, but warn that line creation might fail
      }
      
      console.log('Retrieved newly created quotation:', { 
        id: newQuotationId, 
        number: newQuotationNumber 
      })
      
      // Step 3: Get the original quotation lines
      console.log('Getting lines for original quotation:', quotationData.mserp_salesquotationnumber)
      console.log('Full original quotation data for reference:', quotationData)
      
      const originalLinesResult = await apiService.getQuotationLines(quotationData.mserp_salesquotationnumber)
      console.log('Original lines result:', originalLinesResult)
      console.log('Original lines data length:', originalLinesResult.data ? originalLinesResult.data.length : 'No data property')
      
      if (originalLinesResult.success && originalLinesResult.data && originalLinesResult.data.length > 0) {
        console.log(`Found ${originalLinesResult.data.length} lines to duplicate`)
        console.log('Original lines data:', originalLinesResult.data)
        
        // Check for duplicate item numbers in the original data
        const itemNumbers = originalLinesResult.data.map(line => line.mserp_itemnumber)
        const uniqueItemNumbers = [...new Set(itemNumbers)]
        console.log('Item numbers found:', itemNumbers)
        console.log('Unique item numbers:', uniqueItemNumbers)
        if (itemNumbers.length !== uniqueItemNumbers.length) {
          console.warn('DUPLICATE ITEM NUMBERS DETECTED in original lines!')
        }
        
        // Step 4: Duplicate each quotation line sequentially to avoid conflicts
        console.log('Creating lines sequentially to avoid conflicts...')
        const lineResults = []
        
        for (let index = 0; index < originalLinesResult.data.length; index++) {
          const line = originalLinesResult.data[index]
          console.log(`\n=== PROCESSING LINE ${index + 1} ===`)
          console.log('Original line item number:', line.mserp_itemnumber)
          console.log('Original line ID:', line.mserp_salesquotationlinev2entityid)
          console.log('Original line data summary:', {
            itemNumber: line.mserp_itemnumber,
            description: line.mserp_linedescription,
            quantity: line.mserp_requestedsalesquantity,
            price: line.mserp_salesprice
          })
          
          // Copy ALL fields from the original line
          const duplicateLine = { ...line }
          
          // ONLY remove fields that must be auto-generated by Dataverse
          delete duplicateLine.mserp_recid;                        // Auto-generated record ID
          delete duplicateLine.mserp_inventtransid;                // Auto-generated inventory transaction ID
          delete duplicateLine.mserp_salesquotationlinev2entityid; // Auto-generated line ID
          
          // Change quotation number to the new quotation
          duplicateLine.mserp_salesquotationnumber = newQuotationNumber;
          
          console.log(`✓ Removed mserp_recid, mserp_inventtransid, and line ID, updated quotation number to ${newQuotationNumber}`);
          
          console.log(`\n=== SENDING LINE ${index + 1} TO API ===`)
          console.log('Quotation Number:', newQuotationNumber)
          console.log('Item Number:', duplicateLine.mserp_itemnumber)
          console.log('Line Creation Sequence (mserp_linecreationsequencenumber):', duplicateLine.mserp_linecreationsequencenumber)
          console.log('Line Number (mserp_linenum):', duplicateLine.mserp_linenum)
          console.log('Line Number (mserp_linenumber):', duplicateLine.mserp_linenumber)
          console.log('RecId (should be undefined):', duplicateLine.mserp_recid)
          console.log('InventTransId (should be undefined):', duplicateLine.mserp_inventtransid)
          console.log('COMPLETE line data being sent:')
          console.log(JSON.stringify(duplicateLine, null, 2))
          
          // Check for fields that might be causing uniqueness issues
          const potentialUniqueFields = {
            quotationNumber: duplicateLine.mserp_salesquotationnumber,
            itemNumber: duplicateLine.mserp_itemnumber,
            lineNumber: duplicateLine.mserp_linenumber,
            lineNum: duplicateLine.mserp_linenum,
            recId: duplicateLine.mserp_recid,
            lineCreationSequence: duplicateLine.mserp_linecreationsequencenumber,
            inventTransId: duplicateLine.mserp_inventtransid,
            lineId: duplicateLine.mserp_salesquotationlinev2entityid
          }
          console.log('Potential unique constraint fields:', potentialUniqueFields)
          
          // Filter out undefined values to see what's actually being sent
          const actualFields = Object.entries(potentialUniqueFields)
            .filter(([key, value]) => value !== undefined)
            .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})
          console.log('ACTUAL fields being sent that could cause uniqueness issues:', actualFields)
          
          try {
            console.log(`Creating line ${index + 1} of ${originalLinesResult.data.length}...`)
            const lineResult = await apiService.addQuotationLine(newQuotationNumber, duplicateLine)
            console.log(`Line ${index + 1} creation result:`, lineResult)
            lineResults.push(lineResult)
            console.log(`Line ${index + 1} completed:`, lineResult.success ? 'SUCCESS' : `FAILED: ${lineResult.error}`)
          } catch (error) {
            console.error(`\n=== DETAILED ERROR FOR LINE ${index + 1} ===`)
            console.error('Error object:', error)
            console.error('Error message:', error.message)
            console.error('Error stack:', error.stack)
            
            // Try to parse JSON error if it's a string
            try {
              if (typeof error.message === 'string' && error.message.includes('{')) {
                const jsonStart = error.message.indexOf('{')
                const jsonPart = error.message.substring(jsonStart)
                const parsedError = JSON.parse(jsonPart)
                console.error('Parsed Dataverse error:', JSON.stringify(parsedError, null, 2))
              }
            } catch (parseError) {
              console.error('Could not parse error as JSON:', parseError)
            }
            
            const errorResult = { success: false, error: error.message }
            lineResults.push(errorResult)
            console.log(`Line ${index + 1} completed: FAILED: ${error.message}`)
          }
          
          // Add a small delay between line creations to ensure Dataverse processes them properly
          if (index < originalLinesResult.data.length - 1) {
            console.log('Waiting 200ms before next line...')
            await new Promise(resolve => setTimeout(resolve, 200)) // 200ms delay
          }
        }
        
        // Check results and log detailed information
        const successfulLines = lineResults.filter(result => result.success)
        const failedLines = lineResults.filter(result => !result.success)
        
        console.log('Line creation results:', {
          total: lineResults.length,
          successful: successfulLines.length,
          failed: failedLines.length,
          successfulLines: successfulLines.map(r => r.data),
          failedLines: failedLines.map(r => ({ error: r.error, details: r }))
        })
        
        if (failedLines.length > 0) {
          console.warn('Some quotation lines failed to duplicate:', failedLines.map(r => r.error))
          // Continue anyway - the quotation header was created successfully
        }
        
        console.log(`Successfully duplicated ${successfulLines.length} of ${lineResults.length} quotation lines`)
      } else {
        console.log('No quotation lines found to duplicate')
      }
      
      return newQuotationResult
    } catch (error) {
      console.error('Error duplicating quotation:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async addQuotationLine(quotationId, lineData) {
    try {
      return await apiService.addQuotationLine(quotationId, lineData)
    } catch (error) {
      console.error('Error adding quotation line:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  // Update quotation lines by sending them all at once
  async updateQuotationLines(quotationNumber, lines) {
    try {
      return await apiService.updateQuotationLines(quotationNumber, lines)
    } catch (error) {
      console.error('Error updating quotation lines:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  // Mock mode check - frontend always uses live data via API
  isMockMode() {
    return false
  }

  async getQuotationLines(quotationNumber) {
    try {
      return await apiService.getQuotationLines(quotationNumber)
    } catch (error) {
      console.error('Error fetching quotation lines:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async deleteQuotationLine(lineId) {
    try {
      return await apiService.deleteQuotationLine(lineId)
    } catch (error) {
      console.error('Error deleting quotation line:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async deleteQuotation(quotationNumber) {
    try {
      return await apiService.deleteQuotation(quotationNumber)
    } catch (error) {
      console.error('Error deleting quotation:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async getContactPersons() {
    try {
      // IMPORTANT: Backend must filter mserp_smmcontactpersonv2entity by logged user's company
      // Same filtering logic as applied to other entities (quotations, etc.)
      return await apiService.getContactPersons()
    } catch (error) {
      console.error('Error fetching contact persons:', error)
      return { data: [], success: false, error: error.message }
    }
  }

  async getContactPerson(contactPersonId) {
    try {
      // IMPORTANT: Backend must filter mserp_smmcontactpersonv2entity by logged user's company
      // Same filtering logic as applied to other entities (quotations, etc.)
      return await apiService.getContactPerson(contactPersonId)
    } catch (error) {
      console.error('Error fetching contact person:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async updateQuotationHeader(quotationNumber, headerData) {
    try {
      return await apiService.updateQuotationHeader(quotationNumber, headerData)
    } catch (error) {
      console.error('Error updating quotation header:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async getSmmParameters({ refresh = false } = {}) {
    try {
      return await apiService.getSmmParameters({ refresh })
    } catch (error) {
      console.error('Error fetching SMM parameters:', error)
      return { data: null, success: false, error: error.message }
    }
  }

  async getUserLanguage() {
    try {
      return await apiService.getUserLanguage()
    } catch (error) {
      console.error('Error fetching user language:', error)
      return { success: false, language: null, error: error.message }
    }
  }

  async getSalutations() {
    try {
      return await apiService.getSalutations()
    } catch (error) {
      console.error('Error fetching salutations:', error)
      return { success: false, data: [], error: error.message }
    }
  }
}

export const dataService = new DataService()

// Development helper - expose to window for debugging
if (import.meta.env.MODE === 'development') {
  window.dataService = dataService
}
